export type UUID = string;
export type Email = string;
export type ISODate = string;
export type ISODateTime = string;

export type Str = string | null;
export type Num = number | null;
export type Bool = boolean | null;
export type Arr<T> = T[] | null;

export interface B2BProfile {
  pixeling_experience: Num;
  website_conversion_experience: Num;
  average_order_value: Num;
  brand_recognition: Num;
  estimated_time_to_conversion: Num;
  estimated_audience_size: Num;
}

export interface B2CProfile {
  pixeling_experience: Num;
  website_conversion_experience: Num;
  average_order_value: Num;
  brand_recognition: Num;
  estimated_time_to_conversion: Num;
  estimated_audience_size: Num;
}

export interface FullAdvertiserProfile extends AdvertiserProfile {
  id: UUID;
}

export interface AdvertiserProfile {
  name: Str;
  agency_id: Str;
  industry: Str;
  sub_industry: Str;
  url: Str;
  b2b: B2BProfile | null;
  b2c: B2CProfile | null;
}

export interface ChannelSelection {
  channel_name: Str;
  percentage: Num;
  amount: Num;
}

export interface CampaignKPI {
  kpi_name: Str;
  percentage: Num;
  amount: Num;
  kpi_goal: Str;
  kpi_goal_amount: Num;
  channel_selections: Arr<ChannelSelection>;
  channel_exclusions: Arr<string>;
  skip_channel_prediction: Bool;
}

export type ProjectedKPI = CampaignKPI;

export interface OutputRecord {
  business_goal?: Str;
  campaign_id?: Str;
  plan?: Str;
  media_type?: Str;
  channel?: Str;
  campaign_kpi?: Str;
  scenario_id?: UUID;
  channel_budget?: Num;
  channel_percentage?: Num;
  // todo: investigate if it needed
  // percentage?: Num;
  [metric: string]: string | number | null | undefined;
}
export interface ScenarioOutput {
  outdated: Bool;
  data: Arr<OutputRecord>;
  timestamp: Num;
  status_summary: Arr<string>;
  projected_kpis: Arr<ProjectedKPI>;
  budget_in_time: Arr<BudgetInTimeItem>;
}

export interface Scenario {
  name: Str;
  id: UUID;

  gender: Str;
  age_group: Str;
  states: Arr<string>;
  cities: Arr<string>;
  dma: Arr<string>;
  zip_codes: Arr<string>;

  estimated_audience_size: Num;
  start_date: ISODate | null;
  end_date: ISODate | null;
  net_budget: Num;
  gross_budget: Num;
  campaign_kpis: Arr<CampaignKPI>;
  kpi_goal_enabled: boolean;
  owned_by: Str;
  owner: Email | null;
  approved: Bool;
  output: ScenarioOutput | null;
}

export interface Plan {
  name: Str;
  id: UUID;
  owned_by: Str;
  owner: Email | null;
  approved: Bool;
  scenarios: Arr<Scenario>;
}

export interface BusinessGoal {
  name: Str;
  id: UUID;
  campaign_id: Str;
  business_goal: Str;
  b2b_or_b2c: Str;
  owned_by: Str;
  owner: Email | null;
  plans: Arr<Plan>;
}

export interface Project {
  name: string;
  id: UUID;
  agency_name: Str;
  agency_id: Str;
  advertiser_id: UUID | null;
  advertiser_profile: AdvertiserProfile | null;

  owned_by: Str;
  owner: Email | null;

  editors: Arr<Email>;
  viewers: Arr<Email>;

  publish_date: ISODateTime | null;
  last_modified: ISODateTime | null;

  business_goals: Arr<BusinessGoal>;
}

export type ScenarioDateInfo = {
  scenario_id: string;
  start_date: string;
  end_date: string;
};

export type MetricsRowInput = {
  business_goal: string;
  campaign_id: string;
  campaign_kpi: string;
  plan: string;
  channel: string;
  media_type: string;
  scenario_id: string;

  [k: string]: string | number | undefined;
};

export type RecalculatePlanInput = {
  scenario_dates: ScenarioDateInfo[];
  metrics_rows: MetricsRowInput[];
};

export type RecalculatePlanOutput = {
  metrics_rows: MetricsRowInput[];
  budget_in_time: BudgetInTimeItem[];
};

export type BudgetInTimeItem = {
  kpi: string;
  channel: string;
  distribution: BudgetTimeEntry[];
};

export type BudgetTimeEntry = {
  start_date: string;
  end_date: string;
  daily_budget: number;
};

// Project Output Table Types
export type NumericKey =
  | "channel_budget"
  | "channel_percentage"
  | "cpm"
  | "impressions"
  | "cplv"
  | "landing_page_views"
  | "cpp"
  | "purchases"
  | "roas"
  | "cpa"
  | "cpc"
  | "ctr"
  | "clicks"
  | "cpl"
  | "cpcv"
  | "cpi"
  | "cpe"
  | "cpae"
  | "cpb"
  | "cper"
  | "cpia"
  | "cplc"
  | "cppl"
  | "cpsa"
  | "cpsv"
  | "cps"
  | "cpla"
  | "leads"
  | "acquisitions"
  | "app_installs"
  | "completed_views"
  | "engagements"
  | "purchase_value"
  | "app_events"
  | "bookings"
  | "event_response"
  | "intent_actions"
  | "link_clicks"
  | "page_likes"
  | "site_action"
  | "store_visits"
  | "submission_entries"
  | "video_views"
  | "local_action"
  | "vcr";

export const NUMERIC_KEYS = new Set<NumericKey>([
  "channel_budget",
  "channel_percentage",
  "cpm",
  "impressions",
  "cplv",
  "landing_page_views",
  "cpp",
  "purchases",
  "roas",
  "cpa",
  "cpc",
  "ctr",
  "clicks",
  "cpl",
  "cpcv",
  "cpi",
  "cpe",
  "cpae",
  "cpb",
  "cper",
  "cpia",
  "cplc",
  "cppl",
  "cpsa",
  "cpsv",
  "cps",
  "cpla",
  "leads",
  "acquisitions",
  "app_installs",
  "completed_views",
  "engagements",
  "purchase_value",
  "app_events",
  "bookings",
  "event_response",
  "intent_actions",
  "link_clicks",
  "page_likes",
  "site_action",
  "store_visits",
  "submission_entries",
  "video_views",
  "local_action",
  "vcr",
]);

export type GenerateOutputRequestApi = {
  jobId: string;
};
